import type { GoToRule, LogicContainer, LogicRule, Poll, Question } from 'schema/poll';
import { ICState } from 'schema/state';
import z from 'zod';
import { fetchWithTimeout } from '../utils';

type ExternalLogicRules = NonNullable<ICState['externalLogicRules']>;

export function loadExternalLogicRules(poll: Poll, externalLogicRulesApiUrl: string): Promise<ExternalLogicRules> {
  const externalLogicRules = extractExternalLogicRulesFromPoll(poll);

  if (externalLogicRules.length) {
    return fetchExternalLogicRules(poll.id, externalLogicRules, externalLogicRulesApiUrl);
  }

  return Promise.resolve({});
}

export function extractExternalLogicRulesFromPoll(poll: Poll): LogicRule[] {
  const entitiesWithLogicRules = {
    questions: (question: Question) => extractExternalLogicRulesFromQuestion(question),
    finals: extractLogicRulesFromCommonEntity,
    groups: extractLogicRulesFromCommonEntity,
    sets: extractLogicRulesFromCommonEntity,
  } as const;

  const externalLogicRules: LogicRule[] = [];

  for (const [entityProperty, extractor] of Object.entries(entitiesWithLogicRules)) {
    const entities = poll[entityProperty] ?? [];

    for (const entity of entities) {
      externalLogicRules.push(...extractor(entity as any));
    }
  }

  return externalLogicRules;
}

const externalLogicRuleSources = new Set<LogicRule['source']>(['stats', 'segment', 'enrich']);

function extractExternalLogicRulesFromContainer(container: LogicContainer[] | undefined) {
  if (!container) return [];

  return container
    .flatMap<LogicRule>(rule => rule.logicRules)
    .filter(rule => externalLogicRuleSources.has(rule.source));
}

function extractExternalLogicRulesFromQuestion(question: Question) {
  const questionRules = extractLogicRulesFromCommonEntity(question);
  const answerRules = 'answers' in question.data ? extractExternalLogicRulesFromAnswers(question.data.answers) : [];

  return [...questionRules, ...answerRules];
}

function extractExternalLogicRulesFromAnswers(
  answers: Extract<Question, { data: { answers: unknown[] } }>['data']['answers'],
) {
  return answers.flatMap(answer => {
    return 'logicRules' in answer && answer.logicRules
      ? answer.logicRules.filter(rule => externalLogicRuleSources.has(rule.source))
      : [];
  });
}

function extractLogicRulesFromCommonEntity(entity: {
  data: {
    logicRules?: LogicContainer[];
    goToRules?: GoToRule[];
  };
}) {
  const logicRules = extractExternalLogicRulesFromContainer(entity.data.logicRules);
  const goToRules = (entity.data.goToRules ?? []).flatMap(goToRule =>
    extractExternalLogicRulesFromContainer(goToRule.logicRules),
  );
  return [...logicRules, ...goToRules];
}

/**
 * @throws {Exception} - В случае сетевой/серверной ошибки, или несоответствии ответа контракту
 * */
async function fetchExternalLogicRules(
  pollId: string,
  logicRules: LogicRule[],
  externalLogicRulesApiUrl: string,
): Promise<ExternalLogicRules> {
  const url = new URL(externalLogicRulesApiUrl.replace('{id}', pollId));
  if (typeof document !== 'undefined') url.search = new URL(document.URL).search;

  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(logicRules),
    timeout: 5_000,
  });

  if (!response.ok) {
    throw new Error('Failed to fetch external logic rules');
  }

  const data: unknown = await response.json();

  const { computedLogicRules } = z
    .object({
      computedLogicRules: z.array(
        z.object({
          id: z.string(),
          result: z.boolean(),
        }),
      ),
    })
    .parse(data);

  return computedLogicRules.reduce<ExternalLogicRules>((result, rule) => {
    result[rule.id] = rule.result;
    return result;
  }, {});
}
